import React, {
  lazy, Suspense, useEffect, useState,
} from 'react';

import {
  featureFlagCacheIsWarm,
  hasFeatureInLocalStorage,
  verifyFeatureAccess,
} from '../../../../global/featureFlags';

export const CONTACTS_REDESIGN_FLAG = 'contacts_redesign';

// `fetchFeatureFlags` is a bare axios.get with no timeout and there are no global
// axios defaults, so a request that never reaches the server never rejects. Without
// a deadline the cold-cache path below would hold the route blank indefinitely,
// which is the blank surface R7 exists to prevent. On expiry we fall back to legacy
// for the same reason the catch does: the known-good page beats an unproven one.
export const FLAG_LOOKUP_TIMEOUT_MS = 8000;

const LegacyContacts = lazy(() => import('../Contacts'));
const ContactsRedesignPage = lazy(() => import('./RedesignPage'));

// Mirrors InboxRedesign's gating: the cached localStorage value drives the first
// paint so the page does not wait on a round trip, and the server flag converges
// after mount. Contacts has no separate route-access gate, so unlike the inbox
// there is no CannotAccess branch and no loading state.
//
// Exported so the gate can be tested without stubbing either page module —
// mock.module is process-global in Bun, and stubbing the pages collides with the
// suites that exercise them directly.
// Returns { resolved, enabled }. On a warm cache the answer is known synchronously
// and first paint is immediate. On a cold cache nothing renders until the lookup
// settles, so the legacy page never mounts on the flag-on path — mounting it and
// swapping later would unmount an already-open Add Contact modal (the ?add=true deep
// link opens one with no click) and discard whatever had been typed into it. It also
// avoids a duplicate initial fetch and a modal flash.
export const useContactsRedesignAccess = (timeoutMs = FLAG_LOOKUP_TIMEOUT_MS) => {
  // Captured once: the deadline below exists only for the cold path, and a cache that
  // warms mid-flight must not retroactively arm it.
  const [startedWarm] = useState(featureFlagCacheIsWarm);
  const [access, setAccess] = useState(() => (
    startedWarm
      ? { resolved: true, enabled: hasFeatureInLocalStorage(CONTACTS_REDESIGN_FLAG) === true }
      : { resolved: false, enabled: false }
  ));

  useEffect(() => {
    let isMounted = true;
    let timer = null;

    // Only the first answer counts: whichever of the lookup and the deadline lands
    // first resolves the gate, and the loser is ignored rather than flipping the
    // page out from under the user.
    const settle = (enabled) => {
      if (!isMounted) return;
      isMounted = false;
      clearTimeout(timer);
      setAccess({ resolved: true, enabled });
    };

    // Cold start only. A warm cache is already showing a page, so a slow lookup must
    // leave it alone: swapping it to legacy on expiry would unmount an open Add/Edit
    // modal and discard typed input — the exact state loss the cold gate prevents.
    // Cold has nothing on screen to protect, so falling back beats a blank route.
    if (!startedWarm) {
      timer = setTimeout(() => settle(false), timeoutMs);
    }

    verifyFeatureAccess(CONTACTS_REDESIGN_FLAG)
      .then((hasAccess) => settle(hasAccess === true))
      // A failed lookup lands on the legacy page. Falling back to the surface that
      // has been in production for years beats rendering an unproven one on an
      // error path.
      .catch(() => settle(false));

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [timeoutMs, startedWarm]);

  return access;
};

const ContactsRedesignSwitch = (props) => {
  const { resolved, enabled } = useContactsRedesignAccess();

  if (!resolved) return null;

  return (
    <Suspense fallback={null}>
      {React.createElement(enabled ? ContactsRedesignPage : LegacyContacts, props)}
    </Suspense>
  );
};

export default ContactsRedesignSwitch;
